import React, { useState, useEffect } from 'react';
import { onlineManager, RoomPlayer } from '../core/online';
import { GameState, PlayerSeat } from '../core/types';
import { PokerTable } from '../components/Board/PokerTable';
import { classify } from '../core/combos';
import { Sound } from '../core/audio';
import { Globe, Users, PlusCircle, LogIn, Wifi, Copy, Check, Bot, Crown, ArrowRight } from 'lucide-react';

export const OnlineView: React.FC = () => {
  const [isInRoom, setIsInRoom] = useState<boolean>(false);
  const [roomCode, setRoomCode] = useState<string>(() => Math.floor(1000 + Math.random() * 9000).toString());
  const [joinCode, setJoinCode] = useState<string>('');
  const [playerName, setPlayerName] = useState<string>('大杀四方');
  const [selectedMode, setSelectedMode] = useState<'4p' | '6p'>('4p');
  const [players, setPlayers] = useState<RoomPlayer[]>([]);
  const [gameState, setGameState] = useState<GameState>(onlineManager.gameState);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState<boolean>(false);
  const [notification, setNotification] = useState<string | null>(null);
  const [userViewMode, setUserViewMode] = useState<'play' | 'view' | 'open'>('play');
  const [selectedAgentModalSeat, setSelectedAgentModalSeat] = useState<PlayerSeat | null>(null);

  useEffect(() => {
    onlineManager.onStateUpdate = (newGameState, newPlayers, newViewMode) => {
      setGameState({ ...newGameState });
      setPlayers([...newPlayers]);
      if (newViewMode) setUserViewMode(newViewMode);
    };

    onlineManager.onError = (msg) => {
      showToast(msg);
    };

    return () => {
      onlineManager.disconnect();
    };
  }, []);

  const handleCreateRoom = async () => {
    try {
      showToast('正在创建在线房间...');
      const code = await onlineManager.createRoom(roomCode || '8888', playerName, selectedMode);
      setRoomCode(code);
      setPlayers([...onlineManager.players]);
      setGameState({ ...onlineManager.gameState });
      setIsInRoom(true);
      showToast(`房间【${code}】(${selectedMode === '6p' ? '6人团战' : '4人经典'}) 创建成功！`);
    } catch (e: any) {
      showToast(`创建失败: ${e.message}`);
    }
  };

  const handleJoinRoom = async () => {
    try {
      showToast(`正在连接房间【${joinCode}】...`);
      await onlineManager.joinRoom(joinCode, playerName);
      setIsInRoom(true);
      showToast(`成功加入房间【${joinCode}】！`);
    } catch (e: any) {
      showToast(`加入失败: ${e.message || '请检查房间码'}`);
    }
  };

  const handleCopyRoomLink = () => {
    navigator.clipboard.writeText(roomCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    showToast(`房间码 ${roomCode} 已复制！发送给好友即可联机。`);
  };

  const handleCopyAgentCommand = (seat: PlayerSeat, token?: string) => {
    const cmd = `python -m gacp.examples.hermes_agent --room ${roomCode} --seat ${seat} --token ${token || 'gacp_dev'}`;
    navigator.clipboard.writeText(cmd);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    showToast(`智能体启动命令已复制！可在终端运行直连该座位。`);
  };

  const toggleCard = (cardId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(cardId)) next.delete(cardId);
      else next.add(cardId);
      return next;
    });
  };

  const handlePlayMove = () => {
    const myHand = gameState.hands[onlineManager.mySeat] || [];
    const selectedCards = myHand.filter((c) => selectedIds.has(c.id));
    const combo = classify(selectedCards, gameState.levelRank);

    if (!combo) {
      showToast('所选牌不构成任何有效掼蛋牌型！');
      return;
    }

    Sound.playCardPlay();
    onlineManager.submitMove('play', combo);
    setSelectedIds(new Set());
  };

  const handlePassMove = () => {
    Sound.playPass();
    onlineManager.submitMove('pass');
    setSelectedIds(new Set());
  };

  const showToast = (msg: string) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 3000);
  };

  const isHost = onlineManager.isHost;
  const is4p = selectedMode === '4p' || gameState.hands.length === 4;

  return (
    <div className="h-full w-full flex flex-col min-h-0 overflow-hidden relative select-none">
      {/* Notification Toast */}
      {notification && (
        <div className="fixed top-14 left-1/2 -translate-x-1/2 z-50 bg-amber-500 text-slate-950 font-black px-4 py-2 rounded-xl shadow-2xl animate-bounce border-2 border-amber-300 text-xs sm:text-sm">
          {notification}
        </div>
      )}

      {!isInRoom ? (
        /* Lobby View */
        <div className="flex-1 overflow-y-auto p-3 flex flex-col items-center justify-center max-w-xl mx-auto w-full space-y-4">
          <div className="text-center space-y-1">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-amber-500 to-red-500 flex items-center justify-center mx-auto text-2xl shadow-xl shadow-amber-500/20">
              🌐
            </div>
            <h2 className="text-xl font-black text-white">掼蛋在线多人联机对战</h2>
            <p className="text-xs text-slate-400">
              支持 1 至 6 人实时跨设备联机（4人经典局 / 6人团战局），未坐满位置自动由大师 AI NPC 替补补齐！
            </p>
          </div>

          {/* Nickname input */}
          <div className="w-full bg-slate-900 border border-slate-800 rounded-2xl p-4 space-y-2 shadow-xl">
            <label className="text-xs font-bold text-slate-300">你的牌手昵称：</label>
            <input
              type="text"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-xs font-bold text-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-500"
              placeholder="输入你的昵称..."
            />
          </div>

          {/* Create or Join Options */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
            {/* Create Room Card */}
            <div className="bg-slate-900 border border-amber-500/30 rounded-2xl p-4 space-y-3 shadow-xl flex flex-col justify-between">
              <div className="space-y-1">
                <div className="flex items-center space-x-1.5 text-amber-400 font-bold text-xs">
                  <PlusCircle className="w-4 h-4" />
                  <span>创建专属房间</span>
                </div>
                <p className="text-[11px] text-slate-400">自定义房间码与对战模式</p>
              </div>

              {/* Mode Toggle */}
              <div className="grid grid-cols-2 gap-1.5 bg-slate-950 p-1 rounded-xl border border-slate-800">
                <button
                  type="button"
                  onClick={() => setSelectedMode('4p')}
                  className={`py-1 rounded-lg text-xs font-black transition-all ${
                    selectedMode === '4p'
                      ? 'bg-amber-500 text-slate-950 shadow'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  4人经典局
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedMode('6p')}
                  className={`py-1 rounded-lg text-xs font-black transition-all ${
                    selectedMode === '6p'
                      ? 'bg-amber-500 text-slate-950 shadow'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  6人团战局
                </button>
              </div>

              <div className="space-y-2">
                <input
                  type="text"
                  maxLength={6}
                  value={roomCode}
                  onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-1.5 text-center font-mono text-base font-black text-amber-400"
                  placeholder="如: 8888"
                />

                <button
                  onClick={handleCreateRoom}
                  className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-950 font-black py-2 rounded-xl text-xs shadow transition-transform active:scale-95 flex items-center justify-center gap-1"
                >
                  <span>创建 {selectedMode === '6p' ? '6人' : '4人'} 房间</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Join Room Card */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 space-y-3 shadow-xl flex flex-col justify-between">
              <div className="space-y-1">
                <div className="flex items-center space-x-1.5 text-emerald-400 font-bold text-xs">
                  <LogIn className="w-4 h-4" />
                  <span>输入房间码加入</span>
                </div>
                <p className="text-[11px] text-slate-400">输入好友分享的房间码直接入局</p>
              </div>

              <div className="space-y-2">
                <input
                  type="text"
                  maxLength={6}
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-1.5 text-center font-mono text-base font-black text-emerald-400 uppercase"
                  placeholder="输入4位房间码"
                />

                <button
                  onClick={handleJoinRoom}
                  disabled={!joinCode.trim()}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-slate-950 font-black py-2 rounded-xl text-xs shadow transition-transform active:scale-95 disabled:opacity-30 flex items-center justify-center gap-1"
                >
                  <span>立即加入对局</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* In-Room Table Game View */
        <div className="h-full w-full flex flex-col gap-1.5 min-h-0 overflow-hidden">
          {/* Room Topbar */}
          <div className="shrink-0 bg-slate-900 border border-slate-800 rounded-xl px-2.5 py-1 flex items-center justify-between text-xs shadow-md gap-2 flex-wrap sm:flex-nowrap">
            <div className="flex items-center space-x-2.5">
              <div className="flex items-center space-x-1 text-amber-400 font-black text-xs">
                <Wifi className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
                <span>房间: {roomCode}</span>
                <button
                  onClick={handleCopyRoomLink}
                  className="text-slate-400 hover:text-white p-0.5 rounded"
                  title="复制房间码"
                >
                  {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                </button>
              </div>

              {/* Three View Modes: 对局 | 观战 | 明牌 */}
              <div className="flex items-center bg-slate-950 p-0.5 rounded-lg border border-slate-800 text-[10px] font-bold">
                <button
                  onClick={() => onlineManager.setUserViewMode('play')}
                  className={`px-2 py-0.5 rounded-md transition-all ${
                    userViewMode === 'play'
                      ? 'bg-amber-500 text-slate-950 font-black shadow'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  🎮 对局
                </button>
                <button
                  onClick={() => onlineManager.setUserViewMode('view')}
                  className={`px-2 py-0.5 rounded-md transition-all ${
                    userViewMode === 'view'
                      ? 'bg-sky-500 text-white font-black shadow'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  👁️ 观战
                </button>
                <button
                  onClick={() => onlineManager.setUserViewMode('open')}
                  className={`px-2 py-0.5 rounded-md transition-all ${
                    userViewMode === 'open'
                      ? 'bg-emerald-500 text-slate-950 font-black shadow'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  🎴 明牌
                </button>
              </div>
            </div>

            {/* Player Seats List with Entity Badges */}
            <div className="flex items-center space-x-1.5 overflow-x-auto py-0.5">
              {players.map((p) => (
                <div key={p.seat} className="relative">
                  <div
                    onClick={() => {
                      if (isHost && is4p && p.seat !== 0) {
                        setSelectedAgentModalSeat(p.seat);
                      }
                    }}
                    className={`text-[9.5px] px-2 py-0.5 rounded-lg font-bold flex items-center gap-1 cursor-pointer transition-all ${
                      p.seat === onlineManager.mySeat
                        ? 'bg-amber-500 text-slate-950 font-black'
                        : p.entityType === 'agent'
                        ? 'bg-purple-950 text-purple-200 border border-purple-500/60 shadow-[0_0_8px_rgba(168,85,247,0.4)]'
                        : p.entityType === 'npc'
                        ? 'bg-slate-800 text-slate-300 border border-slate-700'
                        : 'bg-emerald-950 text-emerald-300 border border-emerald-500/50'
                    }`}
                  >
                    <span>
                      {p.entityType === 'agent' ? '🧠 智能体' : p.entityType === 'npc' ? '🤖 人机' : '👤 真人'}
                    </span>
                    <span className="max-w-[70px] truncate">{p.name}</span>
                    {isHost && is4p && p.seat !== 0 && (
                      <span className="text-[8px] text-amber-400 ml-0.5">⚙️</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => {
                onlineManager.disconnect();
                setIsInRoom(false);
              }}
              className="text-rose-400 hover:text-rose-300 text-[11px] font-bold px-2 py-0.5 bg-rose-500/10 rounded-lg border border-rose-500/20 whitespace-nowrap"
            >
              退出房间
            </button>
          </div>

          {/* Seat Configuration Modal (Host Only) */}
          {selectedAgentModalSeat !== null && isHost && (
            <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
              <div className="bg-slate-900 border border-amber-500/40 rounded-2xl p-4 max-w-sm w-full space-y-3 shadow-2xl animate-fade-in">
                <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                  <div className="flex items-center space-x-1.5 text-amber-300 font-bold text-sm">
                    <span>⚙️ 配置座位角色 ({['南', '东', '北', '西'][selectedAgentModalSeat]}家)</span>
                  </div>
                  <button
                    onClick={() => setSelectedAgentModalSeat(null)}
                    className="text-slate-400 hover:text-white"
                  >
                    ✕
                  </button>
                </div>

                <div className="space-y-2">
                  <label className="text-xs text-slate-300 font-bold">选择该座位的对弈实体：</label>
                  <div className="grid grid-cols-3 gap-1.5">
                    <button
                      onClick={() => {
                        onlineManager.setSeatEntityType(selectedAgentModalSeat, 'human');
                        setSelectedAgentModalSeat(null);
                        showToast('已切换为 👤 真人 (等待加入)');
                      }}
                      className="p-2 rounded-xl border border-emerald-500/40 bg-emerald-950/40 text-emerald-300 text-xs font-bold hover:bg-emerald-900/60 flex flex-col items-center gap-1"
                    >
                      <span className="text-base">👤</span>
                      <span>真人</span>
                    </button>
                    <button
                      onClick={() => {
                        onlineManager.setSeatEntityType(selectedAgentModalSeat, 'npc');
                        setSelectedAgentModalSeat(null);
                        showToast('已切换为 🤖 人机 (大师NPC)');
                      }}
                      className="p-2 rounded-xl border border-slate-700 bg-slate-800 text-slate-300 text-xs font-bold hover:bg-slate-700 flex flex-col items-center gap-1"
                    >
                      <span className="text-base">🤖</span>
                      <span>人机</span>
                    </button>
                    <button
                      onClick={() => {
                        onlineManager.setSeatEntityType(selectedAgentModalSeat, 'agent');
                        showToast('已切换为 🧠 智能体 (生成专属 Token)');
                      }}
                      className="p-2 rounded-xl border border-purple-500/60 bg-purple-950/60 text-purple-200 text-xs font-bold hover:bg-purple-900/80 flex flex-col items-center gap-1"
                    >
                      <span className="text-base">🧠</span>
                      <span>智能体</span>
                    </button>
                  </div>
                </div>

                {/* If seat is Agent, show Token and connection command */}
                {players.find((p) => p.seat === selectedAgentModalSeat)?.entityType === 'agent' && (
                  <div className="bg-slate-950 border border-purple-500/30 rounded-xl p-2.5 space-y-2 text-[11px]">
                    <div className="text-purple-300 font-bold flex items-center justify-between">
                      <span>🔑 Agent 认证 Token</span>
                      <span className="font-mono text-amber-300">
                        {players.find((p) => p.seat === selectedAgentModalSeat)?.agentToken || 'gacp_dev'}
                      </span>
                    </div>
                    <div className="text-slate-400 text-[10px]">
                      在 Hermes / OpenClaw 终端运行下方命令即可直连此座位：
                    </div>
                    <button
                      onClick={() =>
                        handleCopyAgentCommand(
                          selectedAgentModalSeat,
                          players.find((p) => p.seat === selectedAgentModalSeat)?.agentToken
                        )
                      }
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-1.5 rounded-lg flex items-center justify-center gap-1 shadow transition-all"
                    >
                      <Copy className="w-3 h-3" />
                      <span>一键复制启动命令</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Table */}
          <div className="flex-1 min-h-0 w-full">
            <PokerTable
              gameState={gameState}
              selectedIds={selectedIds}
              onToggleCard={toggleCard}
              onClearSelection={() => setSelectedIds(new Set())}
              onPlay={handlePlayMove}
              onPass={handlePassMove}
              onAutoHint={() => {}}
              onSendEmoji={(seat, emoji, text) => onlineManager.sendEmoji(emoji, text)}
            />
          </div>
        </div>
      )}
    </div>
  );
};
